import { ShipmentStatus } from "@api/graphql/generated/generated-types";

const OPTIMIZE_ELIGIBLE_STATUSES: ReadonlySet<ShipmentStatus> = new Set([
  ShipmentStatus.Created,
  ShipmentStatus.Assigned,
  ShipmentStatus.Routed,
  ShipmentStatus.InTransit,
]);

export interface OptimizeRoutesStateInput {
  /** Status of every shipment currently selected. */
  selectedStatuses: ShipmentStatus[];
  /**
   * Remaining dispatch-delay seconds for selected shipments still in `Created`.
   * Caller must scope this to Created-only shipments and pass 0 otherwise — the
   * helper does not re-derive the scope from `selectedStatuses` to avoid
   * duplicating the Created-filter that already lives at the dispatch-delay
   * source (`useDispatchDelayCountdown`).
   */
  remainingDispatchDelaySeconds: number;
  /** Human-readable form of `remainingDispatchDelaySeconds`. */
  formattedRemainingDispatchDelay: string;
}

export interface OptimizeRoutesState {
  isDisabled: boolean;
  reason: string | undefined;
}

export function getOptimizeRoutesState({
  selectedStatuses,
  remainingDispatchDelaySeconds,
  formattedRemainingDispatchDelay,
}: OptimizeRoutesStateInput): OptimizeRoutesState {
  if (selectedStatuses.length === 0) {
    return { isDisabled: true, reason: undefined };
  }

  const hasIneligibleStatus = selectedStatuses.some(
    (status) => !OPTIMIZE_ELIGIBLE_STATUSES.has(status)
  );
  if (hasIneligibleStatus) {
    return {
      isDisabled: true,
      reason: "Selection contains orders that can't be optimized",
    };
  }

  if (remainingDispatchDelaySeconds > 0) {
    return {
      isDisabled: true,
      reason: `Some orders are blocked from dispatch for ${formattedRemainingDispatchDelay}`,
    };
  }

  return { isDisabled: false, reason: undefined };
}
